import { useEffect, useMemo, useRef, useState } from "react";
import { EventLog, type LogEntry } from "../core/eventLog";
import { IndexedDbMirror, attachMirror } from "../core/persistence";
import { connect, type ConnectionStatus } from "../core/connection";
import type { SdkGateway } from "../core/gateway";
import { SubscriptionManager } from "../core/subscriptions";
import { RequestRunner } from "../core/requestRunner";
import { AttributeTracker } from "../core/attributes";
import { createLogger, hideBootLog } from "../core/logger";
import { attachLogBridge } from "../core/logBridge";
import { Header } from "./Header";
import { Banners } from "./Banners";
import { ErrorBoundary } from "./ErrorBoundary";
import { TimelinePanel } from "./tabs/TimelinePanel";
import { InspectorPanel } from "./tabs/InspectorPanel";
import { SubscriptionsPanel } from "./tabs/SubscriptionsPanel";
import { RequestsPanel } from "./tabs/RequestsPanel";
import { AttributesPanel } from "./tabs/AttributesPanel";
import { SessionPanel } from "./tabs/SessionPanel";
import { Roster } from "./Roster";
import { distinctContactIds } from "./timelineFilter";
import { probeSession, type AgentSnapshot } from "../core/sessionProbe";
import type { ContactSummary } from "../core/contactId";
import { mergeContacts, onLiveEntry } from "./workspaceEvents";

const log = createLogger("ui.app");

type TabId =
  | "timeline"
  | "inspector"
  | "subscriptions"
  | "requests"
  | "attributes"
  | "session";

const TABS: { id: TabId; label: string }[] = [
  { id: "timeline", label: "Timeline" },
  { id: "inspector", label: "Inspector" },
  { id: "subscriptions", label: "Subscriptions" },
  { id: "requests", label: "Requests" },
  { id: "attributes", label: "Attributes" },
  { id: "session", label: "Session" },
];

type Managers = {
  gateway: SdkGateway;
  manager: SubscriptionManager;
  runner: RequestRunner;
  tracker: AttributeTracker;
};

type Props = {
  /** Forwarded to connect(); overrides the default 5000ms handshake timeout. Test seam. */
  timeoutMs?: number;
};

/**
 * Owns every singleton for the diagnostic session: the EventLog, the workspace connection, the
 * IndexedDB mirror, and (once a gateway exists) the subscription manager / request runner /
 * attribute tracker. Renders the header, status banners, and the tabbed panel set.
 */
export function App({ timeoutMs }: Props): React.JSX.Element {
  const mock = useMemo(
    () => new URLSearchParams(window.location.search).get("mock") === "1",
    [],
  );

  const eventLogRef = useRef<EventLog | undefined>(undefined);
  if (!eventLogRef.current) eventLogRef.current = new EventLog();
  const eventLog = eventLogRef.current;

  // Bridge MUST be attached before connect() so handshake logs land in the timeline.
  const bridgeRef = useRef<(() => void) | undefined>(undefined);
  if (!bridgeRef.current) bridgeRef.current = attachLogBridge(eventLog);

  const mirrorRef = useRef<IndexedDbMirror | undefined>(undefined);
  if (!mirrorRef.current) mirrorRef.current = new IndexedDbMirror();
  const mirror = mirrorRef.current;

  const handleRef = useRef<ReturnType<typeof connect> | undefined>(undefined);
  if (!handleRef.current) {
    handleRef.current = connect({ mock, log: eventLog, timeoutMs });
  }
  const handle = handleRef.current;

  const [status, setStatus] = useState<ConnectionStatus>(handle.status());
  const [entries, setEntries] = useState<readonly LogEntry[]>(() =>
    eventLog.entries(),
  );
  const [entryVersion, setEntryVersion] = useState(0);
  const [selectedSeq, setSelectedSeq] = useState<number | undefined>(undefined);
  const [selectedContactId, setSelectedContactId] = useState<
    string | undefined
  >(undefined);
  const [activeTab, setActiveTab] = useState<TabId>("timeline");
  const [agent, setAgent] = useState<AgentSnapshot>({});
  const [contacts, setContacts] = useState<ContactSummary[]>([]);
  const probedRef = useRef(false);

  const managersRef = useRef<Managers | null>(null);
  const currentGateway = handle.gateway();
  if (currentGateway && managersRef.current?.gateway !== currentGateway) {
    const runner = new RequestRunner(currentGateway, eventLog);
    managersRef.current = {
      gateway: currentGateway,
      manager: new SubscriptionManager(currentGateway, eventLog),
      runner,
      tracker: new AttributeTracker(runner, eventLog),
    };
  }
  const managers = managersRef.current;

  const lastAutoCaptureSeq = useRef(0);

  useEffect(() => {
    hideBootLog();
    const unsubStatus = handle.onChange(setStatus);

    const unsubEntries = eventLog.subscribe((snapshot) => {
      setEntries(snapshot);
      setEntryVersion((v) => v + 1);
      for (const e of snapshot) {
        if (e.seq <= lastAutoCaptureSeq.current) continue;
        lastAutoCaptureSeq.current = e.seq;
        onLiveEntry(e, managersRef.current, setContacts, setSelectedContactId);
      }
    });

    let detach: (() => void) | undefined;
    void (async () => {
      const loaded = await mirror.load();
      if (loaded.length > 0) {
        lastAutoCaptureSeq.current = loaded.reduce(
          (m, e) => Math.max(m, e.seq),
          0,
        );
        eventLog.restore(loaded);
      }
      detach = attachMirror(eventLog, mirror);
    })();

    return () => {
      bridgeRef.current?.();
      unsubStatus();
      unsubEntries();
      detach?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- singletons never change identity
  }, []);

  useEffect(() => {
    if (probedRef.current) return;
    if (status !== "connected" && status !== "mock") return;
    const m = managersRef.current;
    if (!m) return;
    probedRef.current = true;
    void probeSession(m)
      .then((result) => {
        setAgent(result.agent);
        setContacts((prev) => mergeContacts(prev, result.contacts));
        if (!selectedContactId && result.contacts[0]) {
          setSelectedContactId(result.contacts[0].contactId);
        }
      })
      .catch((err: unknown) => {
        log.warn("session probe failed", err);
      });
  }, [status, selectedContactId]);

  const contactIds = useMemo(() => distinctContactIds(entries), [entries]);
  const selectedEntry = entries.find((e) => e.seq === selectedSeq);
  const context = handle.context();
  const errorReason = useMemo(() => {
    if (status !== "error") return undefined;
    const last = [...entries]
      .reverse()
      .find(
        (e) =>
          e.kind === "lifecycle" && e.moduleId === "app" && e.name === "error",
      );
    const payload = last?.payload as { message?: string } | undefined;
    return payload?.message;
  }, [status, entries]);

  return (
    <ErrorBoundary panelName="App">
    <div className="app-shell">
      <Header status={status} eventCount={entries.length} />
      <Banners
        status={status}
        errorReason={errorReason}
        onUseMock={() => {
          const url = new URL(window.location.href);
          url.searchParams.set("mock", "1");
          window.location.assign(url.toString());
        }}
      />
      <Roster
        agent={agent}
        contacts={contacts}
        selectedContactId={selectedContactId}
        onSelectContact={setSelectedContactId}
      />
      <nav className="tab-bar" role="tablist" aria-label="Kit panels">
        {TABS.map((t) => (
          <button
            type="button"
            role="tab"
            key={t.id}
            aria-selected={activeTab === t.id}
            className={activeTab === t.id ? "tab-active" : ""}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>
      <main className="tab-content">
        {activeTab === "timeline" && (
          <ErrorBoundary panelName="Timeline">
            <TimelinePanel
              entries={entries}
              selectedSeq={selectedSeq}
              onSelect={(seq) => {
                setSelectedSeq(seq);
                setActiveTab("inspector");
              }}
            />
          </ErrorBoundary>
        )}
        {activeTab === "inspector" && (
          <ErrorBoundary panelName="Inspector">
            <InspectorPanel entry={selectedEntry} />
          </ErrorBoundary>
        )}
        {activeTab === "subscriptions" && (
          <ErrorBoundary panelName="Subscriptions">
            <SubscriptionsPanel
              manager={managers?.manager}
              version={entryVersion}
            />
          </ErrorBoundary>
        )}
        {activeTab === "requests" && (
          <ErrorBoundary panelName="Requests">
            <RequestsPanel
              runner={managers?.runner}
              selectedContactId={selectedContactId}
              onInvoked={(seq) => setSelectedSeq(seq)}
            />
          </ErrorBoundary>
        )}
        {activeTab === "attributes" && (
          <ErrorBoundary panelName="Attributes">
            <AttributesPanel
              tracker={managers?.tracker}
              contactIds={contactIds}
              selectedContactId={selectedContactId}
              onSelectContact={setSelectedContactId}
              version={entryVersion}
            />
          </ErrorBoundary>
        )}
        {activeTab === "session" && (
          <ErrorBoundary panelName="Session">
            <SessionPanel
              status={status}
              mode={handle.gateway()?.mode}
              appInstanceId={handle.appInstanceId()}
              context={context}
              stats={eventLog.stats()}
              eventLog={eventLog}
            />
          </ErrorBoundary>
        )}
      </main>
    </div>
    </ErrorBoundary>
  );
}
